/* @flow */
import * as React from 'react';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import Moment from 'react-moment';
import type { TranslatorProps } from 'react-i18next';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';

import { viewButton, actionButton } from 'permissions/notifications';
import {
  getNotificationCopy,
  generateActionUrl,
  parseTemplateDates,
  getEventCategory,
} from 'utils/NotificationUtils';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import UserStore from 'stores/UserStore';

import styles from './NotificationPanel.module.scss';
import LoadingWrapper from '../../../components/LoadingWrapper/LoadingWrapper';
import ActionDrawerStore from '../../../stores/ActionDrawerStore';
import { RequireForAccess } from 'react-access';

export type NotificationPanelProps = {
  notification: Object,
  handleAction: Function,
  callback: Function,
};

export type NotificationEventType = $Values<typeof NotificationEventTypes>;
export const NotificationEventTypes = Object.freeze({
  ASSET_RENTAL_EXTENSION_REQUEST: 'AssetRentalExtensionRequest',
});

type InjectedProps = {
  userStore: UserStore,
  actionDrawerStore: ActionDrawerStore,
} & TranslatorProps;

export const variantIcon = {
  approved: '#icon-approve',
  declined: '#icon-declined',
  request: '#icon-archive',
  request_rental_extension: '#icon-extend-rental',
  info: '#icon-information',
  warning: '#icon-error',
};

const variantButtons = {
  view: 'default',
  accept: 'primary',
  approve: 'primary',
  merge: 'primary',
  decline: 'secondary',
};

@translate()
@inject('userStore', 'actionDrawerStore')
@observer
export default class NotificationPanel extends ComponentWithInjectedProps<
  NotificationPanelProps,
  {},
  InjectedProps
> {
  @observable isExpanded = true;

  renderLink(button: React.Element<*>, action: Object, index: number) {
    const url = generateActionUrl(action);

    return url ? (
      <Link key={index.toString()} to={url}>
        {button}
      </Link>
    ) : (
      button
    );
  }

  async action(action, notification, callback, loadingKey) {
    const { handleAction, actionDrawerStore } = this.props;

    actionDrawerStore.setLoadingEntry(loadingKey, true);

    await handleAction(action, notification.template_values, action.fields, callback, loadingKey);

    actionDrawerStore.setLoadingEntry(loadingKey, true);
  }

  renderButtons = (action: Object, index: number) => {
    const {
      t,
      notification,
      callback,
      userStore: { isReadOnly },
    } = this.props;
    const isLink = action.type === 'review' || action.type === 'view';
    const permissions = isLink ? viewButton : actionButton;

    const button = (
      <RequireForAccess permissions={permissions}>
        <LoadingWrapper auto={false} type={'actionable-notification'} key={index.toString()}>
          {loadingKey => (
            <Button
              variant="contained"
              color={variantButtons[action.type]}
              disabled={!isLink && isReadOnly}
              onClick={
                isLink ? null : () => this.action(action, notification, callback, loadingKey)
              }
            >
              {action.type === 'view' && action.namespace === 'notification'
                ? t('general:actions.review')
                : isLink
                ? t(`general:actions.${action.type}_${action.namespace}`)
                : t(`general:actions.${action.type}`)}
            </Button>
          )}
        </LoadingWrapper>
      </RequireForAccess>
    );

    return isLink ? this.renderLink(button, action, index) : button;
  };

  render() {
    const { t, notification } = this.props;
    const eventCategory = getEventCategory(notification);
    const hasButtons = Boolean(notification.actions.length);
    const isClickable = hasButtons ? styles['is-clickable'] : '';
    const Icon = variantIcon[eventCategory];
    const templateValues = { ...notification.template_values };
    const titleCopyInfo = getNotificationCopy(notification, 'Title');
    const bodyCopyInfo = getNotificationCopy(notification, 'Body');
    templateValues.assets =
      Array.isArray(templateValues.assets) &&
      templateValues.assets &&
      templateValues.assets.map(item => item.asset_number).join(', ');

    return (
      <li className={`${styles['notification-panel']} ${styles[eventCategory]}`}>
        <div
          className={`${styles['notification-content']} ${isClickable}`}
          onClick={() => {
            this.isExpanded = hasButtons ? !this.isExpanded : this.isExpanded;
          }}
        >
          <div className={styles['notification-header']}>
            <div className={styles['notification-title']}>
              <svg>
                <use xlinkHref={Icon} />
              </svg>
              <h3>
                {t(titleCopyInfo.key, {
                  ...notification,
                  ...notification.template_values,
                  ...titleCopyInfo.options,
                })}
              </h3>
              <span>
                &nbsp;-&nbsp;
                <Moment date={notification.created_at} format="MMMM DD, YYYY - hh:mm a" />
              </span>
            </div>
            {hasButtons && (
              <div className={styles['expand-icon']}>
                <svg>
                  <use xlinkHref={this.isExpanded ? '#icon-chevron-down' : '#icon-chevron-up'} />
                </svg>
              </div>
            )}
          </div>
          <div className={styles['notification-body']}>
            <p>
              {t(bodyCopyInfo.key, {
                ...notification,
                ...parseTemplateDates(templateValues),
                ...bodyCopyInfo.options,
              })}
            </p>
          </div>
        </div>
        {hasButtons && (
          <Collapse
            in={this.isExpanded}
            timeout="auto"
            unmountOnExit={true}
            className={styles['notification-button-bar']}
          >
            {notification.actions
              .filter((act: Object) => {
                return act.type !== 'update' && act.type !== 'merge';
              })
              .map(this.renderButtons)}
          </Collapse>
        )}
      </li>
    );
  }
}
